import { useEffect, useMemo, useState } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { AlertCircle, CheckCircle2, ChevronLeft, ChevronRight, Cloud, Folder, FolderOpen, LoaderCircle, RefreshCw, Trash2 } from "lucide-react"
import { toast } from "sonner"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { api } from "@/lib/api"
import type { CloudContentSourceSummary, CloudProvider, CloudRemoteFolder } from "@/lib/types"
import { cn } from "@/lib/utils"

const PROVIDER_LABELS: Record<CloudProvider, string> = {
  google_drive: "Google Drive",
  onedrive: "OneDrive",
  dropbox: "Dropbox",
}

type FolderTrailItem = CloudRemoteFolder

function formatSyncTime(value: string | null) {
  if (!value) return null
  const time = new Date(value)
  return Number.isNaN(time.getTime()) ? null : new Intl.DateTimeFormat("pt-BR", { dateStyle: "short", timeStyle: "short" }).format(time)
}

function CloudSourceRow({ source, canManage, onSync, onRemove, syncing }: {
  source: CloudContentSourceSummary
  canManage: boolean
  onSync: (id: string) => void
  onRemove: (source: CloudContentSourceSummary) => void
  syncing: boolean
}) {
  const lastSync = formatSyncTime(source.lastSyncedAt)
  const hasError = Boolean(source.lastError)
  return (
    <article className="flex flex-col gap-3 border-b border-border/70 py-4 last:border-b-0 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex min-w-0 items-start gap-3">
        <span className="mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground"><Folder className="size-4" aria-hidden="true" /></span>
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <p className="truncate text-sm font-semibold">{source.folderName}</p>
            <Badge variant="secondary" className="text-[10px]">{source.visibility === "internal" ? "Interno" : "Público"}</Badge>
          </div>
          <p className="muted mt-1 text-xs">{PROVIDER_LABELS[source.provider]} <span aria-hidden="true">·</span> {source.fileCount} arquivo(s)</p>
          <p className={cn("mt-1.5 flex items-center gap-1 text-xs", hasError ? "text-destructive" : lastSync ? "text-success" : "muted")}>
            {hasError ? <AlertCircle className="size-3.5" aria-hidden="true" /> : lastSync ? <CheckCircle2 className="size-3.5" aria-hidden="true" /> : <LoaderCircle className="size-3.5" aria-hidden="true" />}
            {hasError ? `Falha na sincronização: ${source.lastError}` : lastSync ? `Sincronizada ${lastSync}` : "Aguardando sincronização inicial"}
          </p>
        </div>
      </div>
      {canManage && <div className="flex shrink-0 items-center gap-2 pl-12 sm:pl-0">
        <Button size="sm" variant="outline" onClick={() => onSync(source.id)} disabled={syncing} aria-label={`Sincronizar ${source.folderName}`}>
          {syncing ? <LoaderCircle className="mr-1.5 size-3.5 animate-spin" aria-hidden="true" /> : <RefreshCw className="mr-1.5 size-3.5" aria-hidden="true" />}
          Sincronizar
        </Button>
        <Button size="sm" variant="ghost" className="text-muted-foreground hover:text-destructive" onClick={() => onRemove(source)} aria-label={`Remover ${source.folderName}`}>
          <Trash2 className="size-4" aria-hidden="true" />
        </Button>
      </div>}
    </article>
  )
}

export function CloudContentSourcesPanel({ chatbotId, canManage }: { chatbotId: string; canManage: boolean }) {
  const queryClient = useQueryClient()
  const queryKey = ["cloud-content-sources", chatbotId]
  const query = useQuery({ queryKey, queryFn: () => api.listCloudContentSources(chatbotId), refetchInterval: 30_000 })
  const [connectionId, setConnectionId] = useState("")
  const [folderTrail, setFolderTrail] = useState<FolderTrailItem[]>([])
  const [selectedFolder, setSelectedFolder] = useState<CloudRemoteFolder | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<CloudContentSourceSummary | null>(null)

  const currentConnection = query.data?.connections.find(({ id }) => id === connectionId)
  const parentFolderId = folderTrail.at(-1)?.id
  const foldersQuery = useQuery({
    queryKey: ["cloud-content-folders", chatbotId, currentConnection?.id, parentFolderId],
    queryFn: () => currentConnection ? api.listCloudFolders(chatbotId, currentConnection.provider, currentConnection.id, parentFolderId) : Promise.resolve({ folders: [] }),
    enabled: canManage && Boolean(currentConnection),
  })
  const selectedPath = useMemo(() => [...folderTrail.map(({ name }) => name), ...(selectedFolder ? [selectedFolder.name] : [])].join(" / "), [folderTrail, selectedFolder])

  useEffect(() => {
    if (!query.data || !canManage) return
    if (!query.data.connections.some(({ id }) => id === connectionId)) {
      setConnectionId(query.data.connections[0]?.id ?? "")
      setFolderTrail([])
      setSelectedFolder(null)
    }
  }, [canManage, connectionId, query.data])
  useEffect(() => {
    if (typeof window === "undefined") return
    const params = new URLSearchParams(window.location.search)
    const result = params.get("cloud")
    if (!result) return
    if (result === "connected") toast.success("Conta conectada. Agora escolha uma pasta para este assistente.")
    else if (result === "cancelled") toast.info("Conexão com o provedor cancelada.")
    else toast.error("Não foi possível concluir a conexão em nuvem.")
    params.delete("cloud")
    const search = params.toString()
    window.history.replaceState({}, "", `${window.location.pathname}${search ? `?${search}` : ""}${window.location.hash}`)
  }, [])

  const refreshSources = () => queryClient.invalidateQueries({ queryKey })
  const addMutation = useMutation({
    mutationFn: () => {
      if (!connectionId || !selectedFolder) throw new Error("Selecione uma pasta primeiro.")
      return api.addCloudContentSource(chatbotId, { connectionId, remoteFolderId: selectedFolder.id, folderName: selectedFolder.name })
    },
    onSuccess: () => { setSelectedFolder(null); void refreshSources(); toast.success("Pasta conectada. A primeira sincronização será iniciada em instantes.") },
    onError: (error) => toast.error(error instanceof Error ? error.message : "Não foi possível conectar a pasta."),
  })
  const syncMutation = useMutation({
    mutationFn: (sourceId: string) => api.queueCloudContentSync(chatbotId, sourceId),
    onSuccess: () => { void refreshSources(); toast.success("Sincronização adicionada à fila.") },
    onError: (error) => toast.error(error instanceof Error ? error.message : "Não foi possível iniciar a sincronização."),
  })
  const removeMutation = useMutation({
    mutationFn: (sourceId: string) => api.removeCloudContentSource(chatbotId, sourceId),
    onSuccess: () => { setDeleteTarget(null); void refreshSources(); toast.success("Pasta removida; o conteúdo deixou de alimentar o assistente.") },
    onError: (error) => toast.error(error instanceof Error ? error.message : "Não foi possível remover a pasta."),
  })

  if (query.isPending) return <div className="space-y-3" aria-label="Carregando fontes em nuvem"><div className="h-20 animate-pulse rounded-xl bg-muted/50" /><div className="h-32 animate-pulse rounded-xl bg-muted/50" /></div>
  if (query.isError) return <Alert variant="destructive"><AlertCircle className="size-4" /><AlertDescription>{query.error instanceof Error ? query.error.message : "Não foi possível carregar as fontes em nuvem."}</AlertDescription></Alert>
  const data = query.data
  const folders = foldersQuery.data?.folders ?? []
  const providersConfigured = data.providers.some(({ configured }) => configured)

  return (
    <div className="space-y-5">
      <Card className="border-border/70 shadow-none">
        <CardHeader className="flex flex-row items-start justify-between gap-3 space-y-0 pb-3">
          <div>
            <CardTitle className="flex items-center gap-2 text-sm"><Cloud className="size-4 text-muted-foreground" aria-hidden="true" /> Pastas conectadas</CardTitle>
            <p className="muted mt-1 text-xs">Arquivos compatíveis são indexados para este assistente. A sincronização automática acontece a cada hora.</p>
          </div>
          <Badge variant="secondary" className="shrink-0 tabular-nums">{data.sources.length}</Badge>
        </CardHeader>
        <CardContent className="pt-0">
          {data.sources.length ? data.sources.map((source) => <CloudSourceRow key={source.id} source={source} canManage={canManage && data.canManage} syncing={syncMutation.isPending && syncMutation.variables === source.id} onSync={(id) => syncMutation.mutate(id)} onRemove={setDeleteTarget} />) : (
            <div className="flex flex-col items-center border-t border-border/70 py-8 text-center">
              <span className="flex size-10 items-center justify-center rounded-xl bg-muted text-muted-foreground"><FolderOpen className="size-5" aria-hidden="true" /></span>
              <p className="mt-3 text-sm font-semibold">Nenhuma pasta conectada</p>
              <p className="muted mt-1 max-w-sm text-xs">Conecte uma pasta para manter documentos do assistente sincronizados sem enviar cópias manualmente.</p>
            </div>
          )}
        </CardContent>
      </Card>

      {canManage && data.canManage ? (
        <Card className="border-border/70 shadow-none">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm">Adicionar pasta de nuvem</CardTitle>
            <p className="muted mt-1 text-xs">As fontes começam como internas. Os arquivos originais permanecem no provedor.</p>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-wrap gap-2">
              {data.providers.map(({ provider, configured }) => (
                <Button key={provider} size="sm" variant="outline" disabled={!configured} onClick={() => { window.location.assign(api.cloudContentConnectUrl(chatbotId, provider)) }}>
                  {PROVIDER_LABELS[provider]} <span className="ml-1 text-[10px] text-muted-foreground">{configured ? "Conectar" : "Não configurado"}</span>
                </Button>
              ))}
            </div>
            {!providersConfigured && <Alert><AlertDescription className="text-xs">Os provedores ainda não têm credenciais OAuth configuradas no servidor.</AlertDescription></Alert>}
            {!data.connections.length ? (
              <p className="muted border-t border-border/70 pt-3 text-xs">Depois de conectar uma conta, escolha uma pasta para vincular ao assistente.</p>
            ) : (
              <div className="grid gap-4 lg:grid-cols-[minmax(220px,.8fr)_minmax(0,1.2fr)]">
                <div className="space-y-2">
                  <label htmlFor="cloud-connection" className="text-xs font-semibold">Conta conectada</label>
                  <Select value={connectionId} onValueChange={(value) => { setConnectionId(value); setFolderTrail([]); setSelectedFolder(null) }}>
                    <SelectTrigger id="cloud-connection" className="w-full"><SelectValue placeholder="Selecione uma conta" /></SelectTrigger>
                    <SelectContent>
                      {data.connections.map((connection) => <SelectItem key={connection.id} value={connection.id}>{PROVIDER_LABELS[connection.provider]}{connection.accountEmail ? ` · ${connection.accountEmail}` : ""}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  {currentConnection && <div className="rounded-lg border border-border/70 p-3">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-xs font-semibold">Pasta disponível</p>
                      {folderTrail.length > 0 && <Button size="sm" variant="ghost" onClick={() => { setFolderTrail((trail) => trail.slice(0, -1)); setSelectedFolder(null) }}><ChevronLeft className="mr-1 size-3.5" aria-hidden="true" /> Voltar</Button>}
                    </div>
                    <nav className="mt-2" aria-label="Caminho da pasta">
                      <ol className="flex flex-wrap items-center gap-1 text-[11px]">
                        <li><button type="button" className="font-medium text-primary hover:underline" onClick={() => { setFolderTrail([]); setSelectedFolder(null) }}>Início</button></li>
                        {folderTrail.map((item, index) => <li key={`${item.id}-${index}`} className="inline-flex items-center gap-1 text-muted-foreground">
                          <ChevronRight className="size-3" aria-hidden="true" />
                          {index === folderTrail.length - 1 ? <span aria-current="location">{item.name}</span> : <button type="button" className="hover:text-primary hover:underline" onClick={() => { setFolderTrail((trail) => trail.slice(0, index + 1)); setSelectedFolder(null) }}>{item.name}</button>}
                        </li>)}
                      </ol>
                    </nav>
                    {foldersQuery.isPending ? <p className="muted mt-3 text-xs">Carregando pastas…</p> : foldersQuery.isError ? <p className="mt-3 text-xs text-destructive">Não foi possível listar as pastas.</p> : folders.length ? (
                      <ul className="mt-3 divide-y divide-border/70">
                        {folders.map((folder) => <li key={folder.id} className="flex items-center justify-between gap-2 py-2 first:pt-0 last:pb-0">
                          <button type="button" className="flex min-w-0 items-center gap-2 truncate text-left text-xs font-medium hover:text-primary" onClick={() => { setFolderTrail((trail) => [...trail, folder]); setSelectedFolder(null) }}>
                            <FolderOpen className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" /><span className="truncate">{folder.name}</span>
                          </button>
                          <Button size="sm" variant="ghost" className="shrink-0 text-xs" onClick={() => setSelectedFolder(folder)}>Selecionar</Button>
                        </li>)}
                      </ul>
                    ) : <p className="muted mt-3 text-xs">Nenhuma subpasta nesta localização.</p>}
                  </div>}
                </div>
                <div className="flex min-h-36 flex-col justify-between gap-4 rounded-xl border border-border/70 bg-muted/20 p-4">
                  <div>
                    <p className="text-xs font-semibold">Pasta escolhida</p>
                    {selectedFolder ? <p className="mt-2 break-words text-sm">{selectedPath}</p> : <p className="muted mt-2 text-xs">Abra uma pasta e selecione a que deseja sincronizar.</p>}
                    <p className="muted mt-2 text-[11px]">O acesso é somente leitura; remover a fonte interrompe a busca do assistente nesse conteúdo.</p>
                  </div>
                  <Button className="self-start" size="sm" disabled={!currentConnection || !selectedFolder || addMutation.isPending} onClick={() => addMutation.mutate()}>
                    {addMutation.isPending && <LoaderCircle className="mr-2 size-3.5 animate-spin" aria-hidden="true" />}
                    Conectar pasta
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      ) : (
        <p className="muted text-xs">Somente o dono do assistente ou um superadmin pode conectar e remover fontes em nuvem.</p>
      )}

      <AlertDialog open={Boolean(deleteTarget)} onOpenChange={(open) => { if (!open) setDeleteTarget(null) }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remover pasta conectada?</AlertDialogTitle>
            <AlertDialogDescription>A pasta <strong>{deleteTarget?.folderName}</strong> será removida deste assistente. Os arquivos indexados deixam de aparecer nas respostas; os originais no provedor não são alterados.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={removeMutation.isPending}>Cancelar</AlertDialogCancel>
            <AlertDialogAction className="bg-destructive text-destructive-foreground hover:bg-destructive/90" disabled={removeMutation.isPending} onClick={() => deleteTarget && removeMutation.mutate(deleteTarget.id)}>
              {removeMutation.isPending && <LoaderCircle className="mr-2 size-4 animate-spin" aria-hidden="true" />}Remover pasta
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
